/**
 * Downsamples data for chart rendering using a max-value bucket selection algorithm
 * This reduces the number of data points while preserving peaks and general shape of the data
 *
 * @param data - Array of data points to downsample
 * @param maxPoints - Maximum number of points to return
 * @param getValue - Function to extract numeric value from data point
 * @returns Downsampled array
 */
export function downsampleData<T>(
  data: T[],
  maxPoints: number,
  getValue: (item: T) => number,
): T[] {
  if (data.length <= maxPoints) return data;

  // Always keep first and last points
  const downsampled: T[] = [data[0]];

  const bucketSize = Math.ceil((data.length - 2) / (maxPoints - 2));

  for (let i = 1; i < maxPoints - 1; i++) {
    const bucketStart = 1 + (i - 1) * bucketSize;
    const bucketEnd = Math.min(bucketStart + bucketSize, data.length - 1);

    // Find point with max value in bucket (preserves peaks)
    let maxPoint = data[bucketStart];
    let maxValue = getValue(maxPoint);

    for (let j = bucketStart + 1; j < bucketEnd; j++) {
      const value = getValue(data[j]);
      if (value > maxValue) {
        maxValue = value;
        maxPoint = data[j];
      }
    }

    downsampled.push(maxPoint);
  }

  // Always keep last point
  downsampled.push(data[data.length - 1]);

  return downsampled;
}

/**
 * Determines if data should be downsampled based on length
 *
 * @param dataLength - Number of data points
 * @param threshold - Threshold above which to downsample (default: 200)
 * @returns Whether downsampling is recommended
 */
export function shouldDownsample(dataLength: number, threshold: number = 200): boolean {
  return dataLength > threshold;
}

/**
 * Gets optimal max points for downsampling based on data length
 *
 * @param dataLength - Number of data points
 * @returns Optimal max points for downsampling
 */
export function getOptimalMaxPoints(dataLength: number): number {
  if (dataLength <= 200) return dataLength;
  if (dataLength <= 500) return 150;
  if (dataLength <= 1000) return 100;
  return 80;
}
